/**
 * stuckDetector.js — a diagnostic, not a feature (2026-09-23).
 *
 * Matt: opening a project in the outline "is inconsistent with the information it will load in
 * nodes and many times I have to reload the page inside the project to populate everything." It
 * could not be reproduced on demand, so this watches real use and writes down each time a section
 * gets stuck, with enough context to find the cause:
 *   - STUCK LOADING: a section still showing "Loading…" after STUCK_MS.
 *   - EMPTY BUT COUNTED: a section whose own header shows a count above zero while its contents
 *     say "No … yet" (the header and the loaded list disagree).
 * Each entry records the section, the project on screen, how the project was reached (straight
 * from a page load, or by switching from home / another project), how long after arriving, and
 * the shape of the project-wide caches the suspected race lives in (null / in-flight / counts).
 *
 * Suspected cause (docs/FLIGHT_PLAN.md "Peek without an iframe"): several project-wide caches are a
 * single slot shared by whatever project is on screen, some loads fire once and never retry, and
 * for concepts and BIM an empty list means both "in flight" and "loaded, none".
 *
 * Read it in the affected tab: window.__ajStuck, or JSON.parse(localStorage['aj-stuck']) — it
 * survives reloads. Capped at 60 entries, one per section per project visit. Scans every 3 s only
 * while the tab is visible. Delete this file and its one call in OutlineView.jsx once fixed.
 */

import { ctx } from './ctx.js'
import { wf, isEmbedded } from './state.js'

const STUCK_MS = 12000
const SCAN_MS = 3000
const MAX = 60
let started = false

function cacheShape(v) {
  if (v === undefined) return 'unset'
  if (v === null) return 'null'
  if (Array.isArray(v)) return 'list:' + v.length
  if (typeof v === 'object') return 'map:' + Object.keys(v).length
  return typeof v
}

export function startStuckDetector() {
  // Embedded copies (pop-up iframes, split screen) share localStorage and would overwrite the
  // top tab's log with their own; only the tab you are actually using records.
  if (started || typeof window === 'undefined' || isEmbedded()) return
  started = true
  let log = []
  try { log = JSON.parse(localStorage.getItem('aj-stuck') || '[]') || [] } catch (_) { log = [] }
  window.__ajStuck = log

  const loadedAt = Date.now()
  let project = null            // project on screen at the last scan
  let arrivedAt = loadedAt      // when that project came on screen
  let arrivedFrom = 'page-load' // 'page-load' | 'home' | 'project:<id>'
  const firstSeen = {}          // section key -> when it was first seen loading
  const reported = new Set()    // section keys already written for this project visit

  function write(kind, node, label) {
    const key = node.getAttribute('data-key') || '(no key)'
    if (reported.has(kind + '|' + key)) return
    reported.add(kind + '|' + key)
    log.push({
      at: new Date().toISOString(), kind, section: key, label,
      project, arrivedFrom, secondsAfterArriving: Math.round((Date.now() - arrivedAt) / 1000),
      page: (ctx.recordId ? 'project page' : 'home'),
      caches: {
        concepts: cacheShape(ctx.conceptCache), bim: cacheShape(ctx.bimCache), criteria: cacheShape(ctx.criteriaCache),
        parcels: cacheShape(ctx.parcelsCache), specs: cacheShape(ctx.specsCache), projSheets: cacheShape(ctx.projSheetCache),
        threads: cacheShape(ctx.threadCache), meetings: cacheShape(ctx.meetingCache), permits: cacheShape(ctx.permitCache),
        contracts: cacheShape(ctx.contractCache), milestones: cacheShape(ctx.state && ctx.state.milestones), phases: cacheShape(ctx.state && ctx.state.phases),
      },
    })
    if (log.length > MAX) log.splice(0, log.length - MAX)
    try { localStorage.setItem('aj-stuck', JSON.stringify(log)) } catch (_) { /* full or blocked */ }
  }

  function scan() {
    if (document.visibilityState !== 'visible') return
    const onScreen = wf.zoomProjectId || ctx.recordId || null
    if (onScreen !== project) {
      arrivedFrom = project ? 'project:' + project : (Date.now() - loadedAt < 5000 ? 'page-load' : 'home')
      project = onScreen; arrivedAt = Date.now()
      reported.clear(); for (const k in firstSeen) delete firstSeen[k]
    }
    const now = Date.now()
    const stillLoading = new Set()
    document.querySelectorAll('#aj-sc-sections .wf-loading, #aj-sc-root .wf-loading').forEach(el => {
      if (!el.offsetParent || !/loading/i.test(el.textContent || '')) return
      const node = el.closest('.wf-node[data-key]')
      if (!node) return
      const key = node.getAttribute('data-key')
      stillLoading.add(key)
      if (!firstSeen[key]) firstSeen[key] = now
      else if (now - firstSeen[key] >= STUCK_MS) {
        const row = node.querySelector(':scope > .wf-row')
        write('stuck-loading', node, ((row && row.innerText) || '').replace(/\s+/g, ' ').trim().slice(0, 80))
      }
    })
    for (const k in firstSeen) if (!stillLoading.has(k)) delete firstSeen[k]

    document.querySelectorAll('#aj-sc-sections .wf-node[data-key], #aj-sc-root .wf-node[data-key]').forEach(node => {
      const row = node.querySelector(':scope > .wf-row')
      const kids = node.querySelector(':scope > .wf-children')
      if (!row || !kids || !kids.offsetParent) return
      const m = (row.innerText || '').replace(/\s+/g, ' ').trim().match(/(\d+)\s*⋯?\s*$/)
      if (!m || Number(m[1]) === 0) return
      const first = (kids.innerText || '').replace(/\s+/g, ' ').trim()
      if (/^No [^.]{1,40}(yet|found)\b/i.test(first) && first.length < 70) write('empty-but-counted', node, (row.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 80) + ' | shows: ' + first)
    })
  }

  setInterval(scan, SCAN_MS)
  startMemoryLog()
}

/* Memory log (2026-09-24): a Cortex tab crashed "Out of Memory" after sitting in the background
   (Matt switched Windows desktops and came back). It would not reproduce in minutes, so this writes
   one sample a minute to localStorage — which survives the crash — so after the next "Aw, Snap" a
   reload can show how memory climbed and what the tab was doing. ~6 hours kept.
   Read: JSON.parse(localStorage['aj-mem']) → [{ at, tab, pingMs, mb, limitMb, visible, ws, project, iframes, nodes }]. */
/* Corrections (2026-09-24, from the memory-audit session): (1) embedded copies of the app (a
   pop-up iframe, split screen) share this origin's localStorage and each used to hold its own rows
   array and overwrite the key, so the log lost samples and filled with iframe 'page-load' rows —
   embedded copies now skip it, and every write re-reads and merges. (2) The Flight Board loop kept
   the renderer at 100% CPU and 1–2 GB while this JS heap stayed ~100 MB, so heap size alone misses
   it; `pingMs` (a MessageChannel round trip, not throttled in a background tab) is the busy-page
   signal — near 0 when idle, 100 ms+ when something is saturating the main thread. `tab` tells two
   open Cortex tabs apart. */
const TAB_ID = Math.random().toString(36).slice(2, 7)
function pingMs() {
  return new Promise(resolve => {
    const t = performance.now()
    const ch = new MessageChannel()
    ch.port1.onmessage = () => { ch.port1.close(); resolve(Math.round(performance.now() - t)) }
    ch.port2.postMessage(0)
  })
}
function startMemoryLog() {
  if (!performance.memory || isEmbedded()) return
  const KEY = 'aj-mem', KEEP = 360
  const append = (row) => {
    let rows = []
    try { rows = JSON.parse(localStorage.getItem(KEY) || '[]') || [] } catch (_) { rows = [] }
    rows.push(row)
    if (rows.length > KEEP) rows.splice(0, rows.length - KEEP)
    try { localStorage.setItem(KEY, JSON.stringify(rows)) } catch (_) { /* full or blocked */ }
  }
  // A new page load after a crash: mark the boundary so the curve before it stays readable.
  append({ at: new Date().toISOString(), tab: TAB_ID, event: 'page-load' })
  const sample = async () => {
    try {
      const params = new URLSearchParams(window.location.search)
      const ping = await pingMs()
      append({
        at: new Date().toISOString(), tab: TAB_ID, pingMs: ping,
        mb: Math.round(performance.memory.usedJSHeapSize / 1e6),
        limitMb: Math.round(performance.memory.jsHeapSizeLimit / 1e6),
        visible: document.visibilityState === 'visible',
        ws: params.get('ws') || '', project: wf.zoomProjectId || ctx.recordId || '',
        iframes: document.querySelectorAll('iframe').length,
        nodes: document.getElementsByTagName('*').length,
      })
    } catch (_) { /* skip this sample */ }
  }
  sample()
  setInterval(sample, 60000)
}
